import { AccountPickerModal } from "./AccountPickerModal";
import { fetchMetaAdsAccounts, selectMetaAdsAccount } from "./api";
import type { MetaAdsAccountsResponse } from "./types";

/**
 * Meta Ads account picker shown after the merchant connects Meta from the
 * Fuentes tab. The OAuth callback returns with `?needs_picker=1` but the
 * credential has no ad account bound yet (display name stays "cuenta pendiente
 * de selección"), so every Meta read fails on "missing selected_account_id".
 * This modal closes that gap: list the visible ad accounts, let the merchant
 * bind one, then refresh the dashboard.
 *
 * A thin config over the shared `AccountPickerModal`. The canonical id is the
 * `act_<number>` `id` field — the select endpoint matches on it and
 * `selected.account_id` stores that same `act_` value. Meta preselects the
 * bound account (re-connect), else the first active one, else the first;
 * inactive accounts render disabled.
 */
export function MetaAccountPickerModal({
  open,
  onClose,
  onSelected,
}: {
  open: boolean;
  onClose: () => void;
  onSelected: () => void;
}) {
  return (
    <AccountPickerModal<MetaAdsAccountsResponse>
      open={open}
      onClose={onClose}
      onSelected={onSelected}
      labelledById="meta-picker-title"
      radioName="meta-account"
      queryKey={["meta-ads-accounts"]}
      fetchOptions={fetchMetaAdsAccounts}
      mapOptions={(data) =>
        data.accounts.map((acct) => ({
          id: acct.id,
          label: acct.name || acct.account_id,
          meta: `${acct.currency} · ID ${acct.account_id}${acct.is_active ? "" : " · inactiva"}`,
          disabled: !acct.is_active,
        }))
      }
      preselect={(data) =>
        data.selected.account_id ||
        data.accounts.find((a) => a.is_active)?.id ||
        data.accounts[0]?.id ||
        ""
      }
      selectOption={selectMetaAdsAccount}
      copy={{
        title: "Elegí la cuenta de Meta Ads",
        loading: "Buscando tus cuentas de Meta Ads…",
        fetchError:
          "No pudimos leer tus cuentas de Meta Ads. Cerrá y volvé a intentar la conexión.",
        emptyError:
          "No encontramos cuentas publicitarias en esta cuenta de Meta. Revisá que tengas acceso a una cuenta de Meta Ads.",
        saveError: "No pudimos guardar la cuenta. Probá de nuevo.",
        confirmLabel: "Usar esta cuenta",
      }}
    />
  );
}
